import { useCallback, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Button, MenuItem, MenuList, Popover, Skeleton } from '../../../ds/components';
import { clsx } from '../../../ds/clsx';
import { usePermissions } from '../../../lib/session';
import { LockedPage } from '../../../shell/ModulePages';
import { useAfterSalesBoard } from '../api';
import type { AfterSalesBoardData, AfterSalesCard } from '../types';
import { LoadError, isForbidden } from '../ui/LoadError';
import { TicketsHeader } from '../ui/TicketsHeader';
import page from '../ui/page.module.css';
import { CreateCaseDialog } from './CreateCaseDialog';
import styles from './after-sales.module.css';

/** After-Sales 03 colours only the urgent due labels (deadline tomorrow/over, report older than a day). */
const dueClass: Record<string, string | undefined> = {
  danger: styles.dueDanger,
  warning: styles.dueWarning,
};

/**
 * `/tickets/after-sales` — After-Sales 03: four columns Gemeldet · Beim Handwerker · Termin · Erledigt (counts from
 * the API), the object filter `?propertyId=` and „Vorgang anlegen". Cards open the case; no drag and drop.
 */
export function AfterSalesBoard() {
  const navigate = useNavigate();
  const permissions = usePermissions();
  const [params, setParams] = useSearchParams();
  const propertyId = params.get('propertyId');
  const board = useAfterSalesBoard(propertyId);
  const [creating, setCreating] = useState(false);

  const setProperty = useCallback(
    (id: string | null) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (id) next.set('propertyId', id);
          else next.delete('propertyId');
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  if (permissions.tool('tickets') !== 'ok' || isForbidden(board.error)) return <LockedPage tool="tickets" />;

  const data = board.data;
  return (
    <div className={clsx(page.page, page.tight)}>
      <TicketsHeader
        eyebrow="Tickets"
        title={data?.title ?? 'After-Sales'}
        line={data?.line}
        tab="after-sales"
        actions={
          <>
            {data ? <PropertyFilter data={data} value={propertyId} onChange={setProperty} /> : null}
            <Button onClick={() => setCreating(true)}>Vorgang anlegen</Button>
          </>
        }
      />

      {board.isError ? (
        <LoadError
          what="After-Sales konnte nicht geladen werden"
          error={board.error}
          retrying={board.isFetching}
          onRetry={() => void board.refetch()}
        />
      ) : null}

      {board.isPending ? (
        <div className={page.skeleton} aria-busy="true">
          <Skeleton variant="block" height={320} />
        </div>
      ) : null}

      {data ? (
        <div className={clsx(styles.board, board.isPlaceholderData && board.isFetching && styles.dimmed)}>
          {data.columns.map((column) => (
            <section key={column.key} className={styles.column} aria-label={column.label}>
              <div className={styles.columnHead}>
                <h2 className={styles.columnLabel}>{column.label}</h2>
                <span className={styles.columnCount}>{column.count}</span>
              </div>
              {data.cards
                .filter((card) => card.status === column.key)
                .map((card) => (
                  <CaseCard key={card.id} card={card} />
                ))}
            </section>
          ))}
        </div>
      ) : null}

      {creating ? (
        <CreateCaseDialog
          propertyId={propertyId}
          onClose={(createdId) => {
            setCreating(false);
            if (createdId) void navigate(`/tickets/after-sales/${createdId}`);
          }}
        />
      ) : null}
    </div>
  );
}

function CaseCard({ card }: { card: AfterSalesCard }) {
  return (
    <Link to={`/tickets/after-sales/${card.id}`} className={styles.card}>
      <div className={styles.cardTitle}>{card.title}</div>
      <div className={styles.cardLine}>{card.line}</div>
      <div className={styles.cardFoot}>
        <span>{card.responsible}</span>
        <span className={clsx(styles.cardDue, dueClass[card.tone])}>{card.dueLabel}</span>
      </div>
    </Link>
  );
}

function PropertyFilter({
  data,
  value,
  onChange,
}: {
  data: AfterSalesBoardData;
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const selected = data.properties.find((property) => property.id === value) ?? null;
  return (
    <>
      <Button
        ref={anchorRef}
        variant="secondary"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={selected ? `Objekt: ${selected.name}` : 'Objekt'}
        onClick={() => setOpen((current) => !current)}
      >
        {selected ? selected.name : 'Objekt'} ▾
      </Button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        placement="bottom-end"
        width={240}
        variant="menu"
        initialFocus="none"
      >
        <MenuList label="Objekt" density="compact" onClose={() => setOpen(false)}>
          <MenuItem
            label="Alle Objekte"
            active={value === null}
            onSelect={() => {
              setOpen(false);
              onChange(null);
            }}
          />
          {data.properties.map((property) => (
            <MenuItem
              key={property.id}
              label={property.name}
              active={property.id === value}
              onSelect={() => {
                setOpen(false);
                onChange(property.id);
              }}
            />
          ))}
        </MenuList>
      </Popover>
    </>
  );
}
